import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import styles from './CalificacionesPage.module.css';

const API_BASE = 'https://eduportalual.com/backend/api/x7Kp9mQ2';

function abrirArchivo(url) {
  const token = localStorage.getItem('token');
  window.open(url + (token ? '?token=' + encodeURIComponent(token) : ''), '_blank');
}

export default function CalificacionesPage() {
  const { id: asigId } = useParams();
  const { user } = useAuth();
  const isProfesor = user?.rol === 'profesor';
  return isProfesor
    ? <VistaProfesor asigId={asigId} />
    : <VistaAlumno   asigId={asigId} />;
}

// ============================================================
//  VISTA PROFESOR
// ============================================================
function VistaProfesor({ asigId }) {
  const [trabajos, setTrabajos]       = useState([]);
  const [selected, setSelected]       = useState(null);
  const [entregas, setEntregas]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [modal, setModal]             = useState(null);
  const [nota, setNota]               = useState('');
  const [comentario, setComentario]   = useState('');
  const [archivoCorr, setArchivoCorr] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState('');
  const [publicando, setPublicando]   = useState(false);

  const cargarEntregas = (trabajo) => {
    if (!trabajo) return;
    setLoading(true);
    const endpoint = trabajo.tipo === 'no_entregable'
      ? `/trabajos/${trabajo.id}/calificaciones-directas`
      : `/trabajos/${trabajo.id}/entregas`;
    api.get(endpoint)
      .then(r => setEntregas(r.data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get(`/asignaturas/${asigId}/trabajos`).then(r => {
      const lista = r.data.data || [];
      setTrabajos(lista);
      if (lista.length) setSelected(lista[0]);
    });
  }, [asigId]);

  useEffect(() => { cargarEntregas(selected); }, [selected]);

  const abrirModal = (entrega) => {
    setModal(entrega);
    setNota(entrega.nota ?? '');
    setComentario(entrega.comentario ?? '');
    setArchivoCorr(null);
    setMsg('');
  };

  const guardarCalif = async e => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('nota', nota);
      fd.append('comentario', comentario);
      if (selected.tipo === 'no_entregable') {
        fd.append('alumno_id', modal.alumno_id);
        await api.post(`/trabajos/${selected.id}/calificaciones-directas`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        if (archivoCorr) fd.append('correccion', archivoCorr);
        // Hostalia bloquea PUT directo — usar method spoofing via POST
        if (modal.estado === 'calificado') fd.append('_method', 'PUT');
        await api.post(`/entregas/${modal.id}/calificacion`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setMsg('✓ Guardado correctamente');
      cargarEntregas(selected);
      setTimeout(() => setModal(null), 1200);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const publicarTodas = async () => {
    if (!window.confirm(`¿Publicar todas las notas de "${selected.titulo}"?`)) return;
    setPublicando(true);
    try {
      await api.post(`/trabajos/${selected.id}/calificaciones/publicar`);
      cargarEntregas(selected);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al publicar');
    } finally { setPublicando(false); }
  };

  const despublicarTodas = async () => {
    if (!window.confirm('¿Retirar la publicación de las notas?')) return;
    try {
      await api.post(`/trabajos/${selected.id}/calificaciones/despublicar`);
      cargarEntregas(selected);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al retirar');
    }
  };

  const calificadas = entregas.filter(e =>
    e.estado === 'calificado' || (e.nota !== null && e.nota !== undefined && e.nota !== '')
  );
  const publicadas  = entregas.filter(e => e.publicada == 1);
  const notas       = calificadas.map(e => parseFloat(e.nota)).filter(n => !isNaN(n));
  const media       = notas.length ? (notas.reduce((a, b) => a + b, 0) / notas.length).toFixed(2) : '—';
  const estadoColor = e => ({ entregado:'#2d7a4f', en_revision:'#c07a1a', calificado:'#1a3a6b' }[e] || '#666');

  return (
    <div className={styles.page}>
      <div className={styles.tabs}>
        {trabajos.map(t => (
          <button key={t.id}
            className={`${styles.tab} ${selected?.id === t.id ? styles.tabActive : ''}`}
            onClick={() => setSelected(t)}>
            {t.titulo}
            {t.tipo === 'no_entregable' && <span className={styles.tabTipo}>📋</span>}
            <span className={styles.tabCount}>{t.total_entregas}</span>
          </button>
        ))}
      </div>

      {selected && (
        <>
          <div className={styles.trabajoHeader}>
            <div>
              <h3 className={styles.trabajoTitulo}>{selected.titulo}</h3>
              <div className={styles.stats}>
                <span className={styles.stat}>
                  {selected.tipo === 'no_entregable' ? '👥' : '📥'} {entregas.length} {selected.tipo === 'no_entregable' ? 'alumnos' : 'entregas'}
                </span>
                <span className={styles.stat}>✅ {calificadas.length} calificadas</span>
                <span className={styles.stat}>👁 {publicadas.length} publicadas</span>
                <span className={styles.stat}>📊 Media: {media}</span>
              </div>
            </div>
            <div className={styles.headerActions}>
              <button className={styles.btnDespublicar} onClick={despublicarTodas}>
                🔒 Retirar notas
              </button>
              <button className={styles.btnPublicar} onClick={publicarTodas} disabled={publicando}>
                {publicando ? 'Publicando...' : '📢 Publicar todas las notas'}
              </button>
            </div>
          </div>

          {loading ? (
            <p className={styles.empty}>Cargando...</p>
          ) : entregas.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyIcon}>📭</p>
              <p className={styles.emptyText}>
                {selected.tipo === 'no_entregable'
                  ? 'No hay alumnos matriculados en esta asignatura.'
                  : 'No hay entregas para este trabajo.'}
              </p>
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Alumno</th>
                    {selected.tipo !== 'no_entregable' && <th>Archivo entregado</th>}
                    <th>Estado</th>
                    <th>Nota</th>
                    <th>Publicada</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {entregas.map(e => (
                    <tr key={e.id || e.alumno_id}>
                      <td><strong>{e.apellidos}, {e.nombre}</strong></td>
                      {selected.tipo !== 'no_entregable' && (
                        <td>
                          <div className={styles.fileCell}>
                            <span className={styles.fileName}>{e.nombre_archivo}</span>
                            {e.nombre_archivo && (
                              <button className={styles.btnVerArchivo}
                                onClick={() => abrirArchivo(`${API_BASE}/entregas/${e.slug || e.id}/descargar`)}>
                                👁 Ver
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                      <td>
                        {selected.tipo === 'no_entregable' ? (
                          <span className={styles.estadoBadge} style={{ '--c': e.nota !== null ? '#1a3a6b' : '#888' }}>
                            {e.nota !== null && e.nota !== undefined && e.nota !== '' ? 'calificado' : 'sin calificar'}
                          </span>
                        ) : (
                          <span className={styles.estadoBadge} style={{ '--c': estadoColor(e.estado) }}>
                            {e.estado?.replace('_', ' ')}
                          </span>
                        )}
                      </td>
                      <td className={styles.notaCell}>
                        {e.nota !== null && e.nota !== undefined && e.nota !== ''
                          ? <strong>{Number(e.nota).toFixed(2)}</strong>
                          : <span className={styles.sinNota}>—</span>}
                      </td>
                      <td>
                        {e.nota !== null && e.nota !== undefined && e.nota !== '' ? (
                          e.publicada == 1
                            ? <span className={styles.publicadaSi}>✅ Sí</span>
                            : <span className={styles.publicadaNo}>🔒 No</span>
                        ) : <span className={styles.sinNota}>—</span>}
                      </td>
                      <td>
                        <button className={styles.btnCalificar} onClick={() => abrirModal(e)}>
                          {e.nota !== null && e.nota !== undefined && e.nota !== '' ? 'Editar' : 'Calificar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {modal && (
        <div className={styles.overlay} onClick={() => setModal(null)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Calificar entrega</h3>
            <p className={styles.modalSub}>{modal.apellidos}, {modal.nombre}</p>
            {modal.nombre_archivo && (
              <div className={styles.archivoAlumno}>
                <span className={styles.archivoIcon}>📄</span>
                <span className={styles.archivoNombre}>{modal.nombre_archivo}</span>
                <button className={styles.btnVerArchivo}
                  onClick={() => abrirArchivo(`${API_BASE}/entregas/${modal.slug || modal.id}/descargar`)}>
                  👁 Ver archivo
                </button>
              </div>
            )}
            <form onSubmit={guardarCalif} className={styles.modalForm}>
              <label className={styles.label}>
                Nota (0 – 10)
                <input type="number" min="0" max="10" step="0.01"
                  value={nota} onChange={e => setNota(e.target.value)}
                  className={styles.input} required />
              </label>
              <label className={styles.label}>
                Comentario (opcional)
                <textarea rows="4" value={comentario}
                  onChange={e => setComentario(e.target.value)}
                  className={styles.textarea}
                  placeholder="Observaciones, puntos de mejora..." />
              </label>
              {selected.tipo !== 'no_entregable' && (
                <label className={styles.label}>
                  Documento de corrección (PDF, DOC, ZIP…)
                  <input type="file" accept=".pdf,.doc,.docx,.zip,.txt"
                    onChange={e => setArchivoCorr(e.target.files[0])} />
                  {modal.nombre_correccion && !archivoCorr && (
                    <span className={styles.corrExistente}>
                      📎 Ya tiene corrección: {modal.nombre_correccion}
                    </span>
                  )}
                </label>
              )}
              {msg && (
                <p className={msg.startsWith('✓') ? styles.success : styles.errorMsg}>{msg}</p>
              )}
              <div className={styles.modalActions}>
                <button type="button" className={styles.btnCancel}
                  onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className={styles.btnGuardar} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar calificación'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
//  VISTA ALUMNO
// ============================================================
function VistaAlumno({ asigId }) {
  const [datos, setDatos]     = useState({ calificaciones: [], nota_ponderada: null, peso_acumulado: 0, todo_calificado: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/asignaturas/${asigId}/mis-calificaciones`)
      .then(r => setDatos(r.data.data || { calificaciones: [], nota_ponderada: null, peso_acumulado: 0 }))
      .finally(() => setLoading(false));
  }, [asigId]);

  const calificaciones = datos.calificaciones || [];
  const notaPonderada  = datos.nota_ponderada;
  const pesoAcumulado  = datos.peso_acumulado || 0;

  const notaColor = n => {
    if (n === null || n === undefined) return 'var(--text-light)';
    if (n >= 9) return '#2d7a4f';
    if (n >= 7) return '#1a3a6b';
    if (n >= 5) return '#c07a1a';
    return '#c0392b';
  };
  const notaLabel = n => {
    if (n === null || n === undefined) return '';
    if (n >= 9) return 'Sobresaliente';
    if (n >= 7) return 'Notable';
    if (n >= 6) return 'Bien';
    if (n >= 5) return 'Aprobado';
    return 'Suspenso';
  };

  if (loading) return <p style={{ color:'var(--text-mid)', padding:'2rem 0' }}>Cargando...</p>;

  const grupos = {};
  const sinCategoria = [];
  calificaciones.forEach(c => {
    if (c.categoria_id) {
      if (!grupos[c.categoria_id]) grupos[c.categoria_id] = { nombre: c.categoria_nombre, peso: c.categoria_peso, items: [] };
      grupos[c.categoria_id].items.push(c);
    } else { sinCategoria.push(c); }
  });

  return (
    <div className={styles.page}>
      {pesoAcumulado > 0 && (
        <div className={styles.notaPonderadaBox}>
          <div className={styles.notaPonderadaLeft}>
            <h3 className={styles.notaPonderadaTitulo}>📊 Nota final ponderada</h3>
            <p className={styles.notaPonderadaSub}>
              Calculada sobre el {pesoAcumulado.toFixed(0)}% de la asignatura evaluado hasta ahora
              {!datos.todo_calificado && ' — pendiente de calificar algunos trabajos'}
            </p>
          </div>
          <div className={styles.notaPonderadaCirculo} style={{ '--nc': notaColor(notaPonderada) }}>
            {notaPonderada !== null ? (
              <>
                <span className={styles.notaNum}>{notaPonderada.toFixed(1)}</span>
                <span className={styles.notaLabelSmall}>{notaLabel(notaPonderada)}</span>
              </>
            ) : (
              <span className={styles.notaNum} style={{ fontSize:'1.2rem' }}>—</span>
            )}
          </div>
        </div>
      )}

      <div className={styles.alumnoList}>
        {Object.values(grupos).map((grupo, gi) => (
          <div key={gi} className={styles.categoriaSection}>
            <div className={styles.categoriaSectionHeader}>
              <span className={styles.categoriaSectionNombre}>{grupo.nombre}</span>
              <span className={styles.categoriaSectionPeso}>{grupo.peso}% de la nota final</span>
            </div>
            {grupo.items.map((c, i) => (
              <TarjetaCalificacion key={i} c={c} notaColor={notaColor} notaLabel={notaLabel} />
            ))}
          </div>
        ))}
        {sinCategoria.map((c, i) => (
          <TarjetaCalificacion key={i} c={c} notaColor={notaColor} notaLabel={notaLabel} />
        ))}
        {calificaciones.length === 0 && (
          <div className={styles.emptyState}>
            <p className={styles.emptyIcon}>📝</p>
            <p className={styles.emptyText}>No hay trabajos publicados en esta asignatura.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function TarjetaCalificacion({ c, notaColor, notaLabel }) {
  const tieneNota = c.nota !== null && c.nota !== undefined;
  return (
    <div className={styles.alumnoCard}>
      <div className={styles.alumnoCardLeft}>
        <h4 className={styles.alumnoTrabajo}>{c.trabajo}</h4>
        <div className={styles.alumnoMeta}>
          {c.fecha_limite && <span>📅 Límite: {new Date(c.fecha_limite).toLocaleDateString('es-ES')}</span>}
          <span>⭐ Nota máx: {c.nota_maxima}</span>
          {c.peso_individual !== null && c.peso_individual !== undefined && (
            <span className={styles.pesoTag}>⚖️ Peso: {c.peso_individual}%</span>
          )}
          {c.tipo === 'no_entregable' && (
            <span className={styles.pesoTag}>📋 No entregable</span>
          )}
        </div>

        {c.nombre_archivo && (
          <div className={styles.archivoEntregado}>
            <span>📄 Entregado: <strong>{c.nombre_archivo}</strong></span>
            <button className={styles.btnVerArchivo}
              onClick={() => abrirArchivo(`${API_BASE}/entregas/${c.entrega_slug || c.entrega_id}/descargar`)}>
              👁 Ver
            </button>
          </div>
        )}

        {c.tipo !== 'no_entregable' && !c.entrega_id ? (
          <span className={styles.estadoTag} style={{ background:'#fdecea', color:'#c0392b' }}>⚠️ No entregado</span>
        ) : !tieneNota ? (
          <span className={styles.estadoTag} style={{ background:'rgba(201,168,76,.12)', color:'var(--gold)' }}>⏳ Pendiente de calificación</span>
        ) : (
          <div className={styles.alumnoCalif}>
            {c.comentario && (
              <div className={styles.comentarioBox}>
                <p className={styles.comentarioLabel}>💬 Comentario del profesor:</p>
                <p className={styles.comentarioTexto}>{c.comentario}</p>
              </div>
            )}
            {c.nombre_correccion && (
              <div className={styles.archivoCorreccion}>
                <span>📎 Corrección: <strong>{c.nombre_correccion}</strong></span>
                <button className={styles.btnVerArchivo}
                  onClick={() => abrirArchivo(`${API_BASE}/calificaciones/${c.calif_id}/correccion`)}>
                  👁 Ver corrección
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.notaCirculo} style={{ '--nc': notaColor(tieneNota ? parseFloat(c.nota) : null) }}>
        {tieneNota ? (
          <>
            <span className={styles.notaNum}>{Number(c.nota).toFixed(1)}</span>
            <span className={styles.notaLabelSmall}>{notaLabel(parseFloat(c.nota))}</span>
          </>
        ) : (
          <span className={styles.notaNum} style={{ fontSize:'1.2rem', color:'var(--text-light)' }}>—</span>
        )}
      </div>
    </div>
  );
}
